import React, { PureComponent } from "react";
import Select from "react-select";

import { Button, ButtonGroup, Icon } from "aible";
import { Popover, OverlayTrigger, Modal } from "react-bootstrap";
import { LineInput } from "components/Inputs/LineInput";

import numeral from "numeral";

import * as Utils from "utils/misc";

import ModelBoxContainer from "containers/Business/ModelBox/ModelBox";
import IntrospectContainer from "containers/Next/Strategy/Introspect";

import { SegmentMetric } from "utils/optimizer/ProjectOptimizer";

import { BusinessConstraintMode, BusinessConstraintDirection } from "containers/Setup/reducer";

import styles from "./style.module.css";

const mOptions = [
  { label: "at least", value: BusinessConstraintDirection.Above },
  { label: "no more than", value: BusinessConstraintDirection.Below }
];

export default class NextBusinessConstraints extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      showSecondaryObjectiveSection: this.props.secondaryBusinessConstraintType ? true : false
    };
  }

  capitalize(s) {
    if (typeof s !== "string") return "";
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  renderValue(unitType, value, isPercent = false) {
    if (isPercent) {
      return (value < 0 ? "-" : "") + numeral(value * 100).format("0.[00]") + "%";
    }

    return (value < 0 ? "-" : "") + unitType + Math.abs(value).toLocaleString("en-US", { maximumFractionDigits: 2 });
  }

  renderConstraintInput(constraint) {
    const {
      runId,
      setup,
      modelGroups,
      setupLabels,
      isOverallSegment,
      updateBusinessConstraintDirection,
      updateBusinessConstraintMode,
      updateBusinessConstraintValue,
      forceDropdown,
      isNext
    } = this.props;

    const transactionLabel = setupLabels && setupLabels.unitType ? setupLabels.unitType : "$";

    const constraintModes = [
      { label: transactionLabel, value: BusinessConstraintMode.Number },
      { label: "%", value: BusinessConstraintMode.Percent }
    ];

    const allDriversAdded =
      modelGroups && modelGroups.drilldownValues && modelGroups.drilldownValues.length > 1
        ? modelGroups.drilldownValues.filter(
            (group) => group && group.setup && group.column !== "Overall" && !group.setup.showImpact
          ).length === 0
        : modelGroups && modelGroups.drilldownValues && modelGroups.drilldownValues.length === 1
          ? modelGroups.drilldownValues.filter((group) => group && group.setup && !group.setup.showImpact).length === 0
          : false;

    const allowUnitDropdown =
      forceDropdown === true || isNext ? true : isOverallSegment ? allDriversAdded : setup && setup.showImpact;

    return (
      <React.Fragment>
        <span style={{ width: "135px", marginRight: "10px" }}>
          <Select
            options={mOptions}
            value={mOptions.find(({ value }) => value == constraint.direction)}
            defaultValue={mOptions[0]}
            onChange={({ value }) => updateBusinessConstraintDirection(runId, constraint.type, value)}
          />
        </span>
        <LineInput
          isMediumBox
          value={
            constraint.mode === BusinessConstraintMode.Percent
              ? Utils.humanize(constraint.value * 100)
              : constraint.value
          }
          onChange={(val) => {
            const value =
              constraint.mode === BusinessConstraintMode.Percent ? Utils.humanize(Number(val) / 100) : Number(val);

            updateBusinessConstraintValue(runId, constraint.type, value);
          }}
        />
        {allowUnitDropdown ? (
          <span style={{ width: "75px", marginRight: "8px" }}>
            <Select
              options={constraintModes}
              onChange={({ value }) => updateBusinessConstraintMode(runId, constraint.type, value)}
              value={constraintModes.find((mode) => mode.value == constraint.mode)}
              isSearchable={false}
            />
          </span>
        ) : (
          <span style={{ marginRight: "5px", marginLeft: "-10px" }}>{"%"}</span>
        )}
        {constraint.mode === BusinessConstraintMode.Percent && (
          <span className={styles.subText}>
            {`of baseline (${this.renderValue(setupLabels.unitType, constraint.convertedValue)})`}
          </span>
        )}
      </React.Fragment>
    );
  }

  renderConstraintText(unitType, constraint) {
    return (
      <React.Fragment>
        <span
          style={{
            fontWeight: "bold",
            marginLeft: "5px",
            marginRight: "5px"
          }}
        >
          {constraint.typeLabel}
        </span>
        <span>{"of " + (mOptions.find(({ value }) => value == constraint.direction) || mOptions[0]).label}</span>
        <span
          style={{
            fontWeight: "bold",
            marginLeft: "5px",
            marginRight: "5px"
          }}
        >
          {this.renderValue(unitType, constraint.value, constraint.mode === BusinessConstraintMode.Percent)}
        </span>
        {constraint.mode === BusinessConstraintMode.Percent && (
          <span
            style={{
              marginRight: "5px"
            }}
          >
            {`of baseline (${this.renderValue(unitType, constraint.convertedValue)})`}
          </span>
        )}
      </React.Fragment>
    );
  }

  renderOverview() {
    const { allBusinessConstraints, profitLabel, overallBusinessConstraints } = this.props;

    const overallTopType = (overallBusinessConstraints.primary || {}).type;

    const onlyOverallStrategy = allBusinessConstraints.every(
      (c) => c.subgroup === "Overall" || c.primary === undefined
    );
    return (
      <React.Fragment>
        <div className={styles.sConstraints} style={{ marginBottom: "5px" }}>
          {overallTopType === SegmentMetric.Revenue || overallTopType === SegmentMetric.Cost ? (
            <React.Fragment>
              {"My overall goal is to reach "}
              {this.renderConstraintText(overallBusinessConstraints.unitType, overallBusinessConstraints.primary)}
            </React.Fragment>
          ) : (
            <React.Fragment>
              {"My overall goal is to maximize "}
              <strong
                style={{
                  fontWeight: "bold",
                  marginLeft: "5px",
                  marginRight: "5px"
                }}
              >
                {profitLabel}
              </strong>
            </React.Fragment>
          )}
          {overallBusinessConstraints.secondary !== undefined && (
            <React.Fragment>
              <span>{"with a secondary goal to reach "}</span>
              {this.renderConstraintText(overallBusinessConstraints.unitType, overallBusinessConstraints.secondary)}
            </React.Fragment>
          )}
        </div>
        {onlyOverallStrategy === false && (
          <div style={{ marginTop: "10px" }}>{"While maintaining these specific goals:"}</div>
        )}
        {allBusinessConstraints
          .filter((c) => c.subgroup !== "Overall" && c.primary !== undefined)
          .sort((a, b) => (a.subgroup > b.subgroup ? 1 : a.subgroup < b.subgroup ? -1 : 0))
          .map((c) => (
            <div key={c.subgroup} className={styles.sConstraints}>
              <React.Fragment>
                <span className={styles.subBullet} style={{ background: c.color }} />
                <span>{c.subgroup + ":"}</span>
                {c.primary.type === SegmentMetric.Profit && c.primary.value === undefined ? (
                  <span>
                    <span style={{ marginLeft: "5px" }}>{"maximize"}</span>
                    <span
                      style={{
                        fontWeight: "bold",
                        marginLeft: "5px",
                        marginRight: "5px"
                      }}
                    >
                      {c.primary.typeLabel}
                    </span>
                  </span>
                ) : (
                  this.renderConstraintText(c.unitType, c.primary)
                )}
              </React.Fragment>
              {c.secondary !== undefined && (
                <React.Fragment>
                  <span>{"with a secondary goal to reach "}</span>
                  {this.renderConstraintText(c.unitType, c.secondary)}
                </React.Fragment>
              )}
            </div>
          ))}
      </React.Fragment>
    );
  }

  render() {
    const {
      allBusinessConstraints,
      optimized,
      runId,
      isOverallSegment,
      primaryConstraint,
      secondaryConstraint,
      isOptimizerWithinBounds,
      updatePrimaryBusinessConstraintType,
      updateSecondaryBusinessConstraintType,
      costLabel,
      revenueLabel,
      profitLabel,
      overallBusinessConstraints,
      overallOnly,
      currentSegment,
      overviewOnly,
      isNext,
      hideModel,
      id,
      hideOverview,
      hasCustomMappings
    } = this.props;

    if (overviewOnly) {
      return this.renderOverview();
    }

    const sOptions = [
      {
        label: revenueLabel,
        value: "Successful Actions",
        type: SegmentMetric.Revenue
      },
      { label: costLabel, value: "Resourcing", type: SegmentMetric.Cost },
      { label: profitLabel, value: "Impact", type: SegmentMetric.Profit },
      { label: "Not set (Optional)", value: "None", type: "NONE" }
    ];

    const handleSecondaryConstraintButton = () => {
      if (this.state.showSecondaryObjectiveSection) {
        updateSecondaryBusinessConstraintType(runId, undefined);
      }
      this.setState({
        showSecondaryObjectiveSection: !this.state.showSecondaryObjectiveSection
      });
    };

    const overallTopType = (overallBusinessConstraints.primary || {}).type;

    const onlyOverallStrategy = allBusinessConstraints.every(
      (c) => c.subgroup === "Overall" || c.primary === undefined
    );

    // if (hasCustomMappings) {
    //   return (
    //     <div>
    //       {this.renderOverview()}
    //     </div>
    //   )
    // }

    return (
      <div style={isNext ? { padding: "20px 20px 0px 20px" } : {}}>
        <div>
          {!hideOverview && (
            <div className={styles.stratTitle}>
              {onlyOverallStrategy ? "What is the strategic focus for your business?" : "Your Strategy"}
            </div>
          )}
          {!hideOverview && this.renderOverview()}
        </div>
        {!isNext && (
          <div className={styles.stratTitle + " " + styles.sSubtitle} style={{ marginTop: "30px" }}>
            {isOverallSegment || overallOnly || !currentSegment
              ? "What is your strategic focus?"
              : `Would you like to add a specific strategy for ${currentSegment.label}?`}
          </div>
        )}
        <div className={styles.sFlex}>
          <ButtonGroup>
            {[SegmentMetric.Revenue, SegmentMetric.Cost, SegmentMetric.Profit].map((metricType) => (
              <OverlayTrigger
                placement={"top"}
                trigger={["hover"]}
                rootClose
                overlay={
                  <Popover id={"popover-positioned-top"}>
                    {sOptions.find(({ type }) => type == metricType).label}
                  </Popover>
                }
                key={metricType}
              >
                <Button
                  active={
                    (isOverallSegment && primaryConstraint === undefined && metricType === SegmentMetric.Profit) ||
                    (primaryConstraint || {}).type === metricType
                  }
                  onClick={() => {
                    if ((primaryConstraint || {}).type !== metricType) {
                      updatePrimaryBusinessConstraintType(runId, metricType);
                    } else if (isOverallSegment !== true) {
                      updatePrimaryBusinessConstraintType(runId, undefined);
                    }
                  }}
                >
                  <Icon type={metricType} size={22} style={{ marginRight: "5px" }} />
                  <div className={styles.constraintButtonText}>
                    {sOptions.find(({ type }) => type == metricType).label}
                  </div>
                </Button>
              </OverlayTrigger>
            ))}
          </ButtonGroup>
        </div>
        {primaryConstraint !== undefined && (
          <div className={styles.minMax}>
            {isOverallSegment !== true && primaryConstraint !== undefined && (
              <div style={{ marginBottom: "10px" }}>
                <a onClick={() => updatePrimaryBusinessConstraintType(runId, undefined)}>{"Remove Strategy"}</a>
              </div>
            )}
            {primaryConstraint.type === SegmentMetric.Profit ? (
              <div className={styles.sConstraints}>
                <span>{"My primary goal is to maximize total "}</span>
                <span style={{ fontWeight: "bold", marginLeft: "5px" }}>{profitLabel}</span>
              </div>
            ) : (
              <div className={styles.sConstraints}>
                <span>{"My primary goal is to reach "}</span>
                <span
                  style={{
                    fontWeight: "bold",
                    marginLeft: "5px",
                    marginRight: "5px"
                  }}
                >
                  {primaryConstraint.typeLabel}
                </span>
                <span
                  style={{
                    marginRight: "5px"
                  }}
                >
                  {"of"}
                </span>
                {this.renderConstraintInput(primaryConstraint)}
              </div>
            )}
          </div>
        )}
        {primaryConstraint !== undefined && (
          <div className={styles.minMax}>
            {this.state.showSecondaryObjectiveSection && (
              <div className={styles.sConstraints}>
                <span>{"My secondary goal is to reach"}</span>
                <span
                  style={{
                    width: "165px",
                    marginRight: "10px",
                    marginLeft: "10px",
                    marginBottom: "5px",
                    marginTop: "5px"
                  }}
                >
                  <Select
                    options={sOptions.filter((s) => s.type !== (primaryConstraint || {}).type)}
                    value={sOptions.find(({ type }) => type == (secondaryConstraint || {}).type) || sOptions[3]}
                    onChange={(val) =>
                      updateSecondaryBusinessConstraintType(runId, val.type == "NONE" ? undefined : val.type)
                    }
                  />
                </span>
                <span
                  style={{
                    marginRight: "10px"
                  }}
                >
                  {" of "}
                </span>
                {secondaryConstraint && this.renderConstraintInput(secondaryConstraint)}
              </div>
            )}
            <div className={styles.sConstraints} style={{ justifyContent: "left", marginTop: "10px" }}>
              <a style={{ color: "#7E69D7" }} onClick={() => handleSecondaryConstraintButton()}>
                {this.state.showSecondaryObjectiveSection
                  ? `Remove secondary constraint`
                  : `I would like to add a secondary constraint`}
              </a>
            </div>
          </div>
        )}
        <div
          className={styles.errors}
          style={
            optimized && optimized.bestModel && isOptimizerWithinBounds !== true
              ? { maxHeight: "50px" }
              : { maxHeight: "0px" }
          }
        >
          {"The Aible Optimizer could not find a solution"}
        </div>
        {isNext && hideModel !== true && (
          <div className={styles.mBox} style={{ left: "27%", top: "110px", width: "440px" }}>
            <ModelBoxContainer id={id} runId={runId} isSmall={true} showSubgroup={false} strategy />
          </div>
        )}
        <IntrospectContainer id={id} />
      </div>
    );
  }
}
